import type { NestedThemeTokens } from './flatten-theme-tokens';
import type { ThemeAssets } from './theme.assets.defaults';

/**
 * Same shape as content-i18n / Experience theme documents
 * (`content/theme/apps/.../tenants/<tenant>.json`, base, rubrics).
 * Only `tokens` (required) and `assets` (optional) are allowed at the root.
 */
export type ExperienceThemeDocument = {
    tokens: NestedThemeTokens;
    assets?: Record<string, string>;
};

export const DEFAULT_DESIGN_TOKENS_PROFILE_ID = 'default';

export type DesignTokensProfileValidationResult =
    | {
          ok: true;
          document: ExperienceThemeDocument;
          name: string;
          tokens: NestedThemeTokens;
          assets?: Record<string, string>;
      }
    | {
          ok: false;
          errors: string[];
      };

const ALLOWED_ROOT_KEYS = new Set(['tokens', 'assets']);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

/** Deep-merge for Experience theme token trees (tenant overlay onto base). */
export const mergeNestedThemeTokens = (
    base: NestedThemeTokens,
    overlay: NestedThemeTokens,
): NestedThemeTokens => {
    const out: NestedThemeTokens = { ...base };
    for (const [key, value] of Object.entries(overlay)) {
        if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            out[key] &&
            typeof out[key] === 'object' &&
            !Array.isArray(out[key])
        ) {
            out[key] = mergeNestedThemeTokens(
                out[key] as NestedThemeTokens,
                value as NestedThemeTokens,
            );
        } else {
            out[key] = value;
        }
    }
    return out;
};

const validateTokenNode = (node: unknown, path: string, errors: string[]): void => {
    if (node === null || node === undefined) {
        errors.push(`${path}: valor nulo no permitido.`);
        return;
    }
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
        return;
    }
    if (Array.isArray(node)) {
        errors.push(`${path}: no se permiten arrays (formato Experience).`);
        return;
    }
    if (!isPlainObject(node)) {
        errors.push(`${path}: tipo de valor no soportado.`);
        return;
    }
    if (!Object.keys(node).length) {
        // Empty nested objects are useless but harmless in overlays; allow.
        return;
    }
    for (const [key, value] of Object.entries(node)) {
        validateTokenNode(value, `${path}.${key}`, errors);
    }
};

const normalizeAssets = (
    raw: Record<string, unknown>,
    errors: string[],
): Record<string, string> | undefined => {
    const assets: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw)) {
        if (typeof value !== 'string') {
            errors.push(`assets.${key}: debe ser string (path/URL de asset Experience).`);
            continue;
        }
        if (!value.trim()) {
            errors.push(`assets.${key}: no puede estar vacío.`);
            continue;
        }
        assets[key] = value;
    }
    return Object.keys(assets).length ? assets : undefined;
};

/**
 * Validates a theme JSON exactly as content-i18n / Experience tenant theme files:
 * `{ "tokens": { ... }, "assets"?: { ... } }`.
 */
export function validateDesignTokensProfile(
    raw: unknown,
    fallbackName = 'Perfil importado',
): DesignTokensProfileValidationResult {
    const errors: string[] = [];

    if (!isPlainObject(raw)) {
        return {
            ok: false,
            errors: ['El archivo debe ser un objeto JSON con forma Experience: { tokens, assets? }.'],
        };
    }

    const unknownKeys = Object.keys(raw).filter((key) => !ALLOWED_ROOT_KEYS.has(key));
    if (unknownKeys.length) {
        errors.push(
            `Claves no permitidas en la raíz (solo "tokens" y "assets"): ${unknownKeys.join(', ')}. ` +
                'El JSON debe ser compatible con content-i18n / Experience (tenant theme).',
        );
    }

    const tokensCandidate = raw['tokens'];
    if (!isPlainObject(tokensCandidate)) {
        errors.push('Falta "tokens" (objeto). Formato requerido: { "tokens": { ... }, "assets"?: { ... } }.');
    } else if (!Object.keys(tokensCandidate).length) {
        errors.push('"tokens" no puede estar vacío.');
    } else {
        validateTokenNode(tokensCandidate, 'tokens', errors);
    }

    let assets: Record<string, string> | undefined;
    if (raw['assets'] !== undefined) {
        if (!isPlainObject(raw['assets'])) {
            errors.push('"assets" debe ser un objeto de strings si está presente.');
        } else {
            assets = normalizeAssets(raw['assets'], errors);
        }
    }

    if (errors.length) {
        return { ok: false, errors };
    }

    const tokens = tokensCandidate as NestedThemeTokens;
    const document: ExperienceThemeDocument = {
        tokens,
        ...(assets ? { assets } : {}),
    };

    return {
        ok: true,
        name: fallbackName,
        tokens,
        assets,
        document,
    };
}

/** Builds an Experience-compatible theme document (no lab-only metadata). */
export function toExperienceThemeDocument(
    tokens: NestedThemeTokens,
    assets?: Partial<ThemeAssets> | Record<string, string> | null,
): ExperienceThemeDocument {
    const document: ExperienceThemeDocument = {
        tokens: tokens ?? {},
    };
    const exportAssets = pickExportableAssets(assets);
    if (exportAssets) {
        document.assets = exportAssets;
    }
    return document;
}

/** Keeps only string asset paths suitable for content-i18n (skips resolved http(s) URLs). */
export function pickExportableAssets(
    assets?: Partial<ThemeAssets> | Record<string, string> | null,
): Record<string, string> | undefined {
    if (!assets) {
        return undefined;
    }
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(assets)) {
        if (typeof value !== 'string' || !value.trim()) {
            continue;
        }
        if (/^https?:\/\//i.test(value) || value.startsWith('url(')) {
            continue;
        }
        out[key] = value;
    }
    return Object.keys(out).length ? out : undefined;
}

export function downloadJsonFile(filename: string, data: unknown): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}
